import { parseActividadApiError } from './actividad-adjuntos';

export type ActividadMailStatus = 'NUEVO' | 'ENVIADO' | 'CERRADO';

export interface ActividadMailAttachment {
  adjuntoItemId?: number;
  nombre?: string;
  contentType?: string;
  archivoId?: number;
}

export interface ActividadMailData {
  subject?: string;
  from?: string;
  to?: string;
  body?: string;
  attachments?: { items?: ActividadMailAttachment[] };
  requeridoParaTerminar?: boolean;
  acciones?: { nombre?: string; descripcion?: string; estado?: string }[];
}

export interface ActividadMailDTO {
  id?: number;
  to?: string | null;
  status?: ActividadMailStatus;
  data?: ActividadMailData | string | unknown;
  usuarioActividadId?: number;
}

export interface ActividadMailFields {
  subject: string;
  from: string;
  to: string;
  body: string;
}

function decodeBytesToText(bytes: number[]): string | null {
  try {
    if (
      bytes.length > 0
      && bytes.every(byte => Number.isInteger(byte) && byte >= 0 && byte <= 255)
    ) {
      return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
    }
  } catch {
    // ignore
  }
  return null;
}

function pickMailString(record: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
      continue;
    }
    if (value != null && typeof value !== 'object') {
      const text = String(value).trim();
      if (text) return text;
    }
  }
  return undefined;
}

function recordToMailData(record: Record<string, unknown>): ActividadMailData | undefined {
  const subject = pickMailString(record, 'subject', 'Subject', 'asunto', 'Asunto');
  const from = pickMailString(record, 'from', 'From', 'de', 'De', 'remitente', 'Remitente');
  const to = pickMailString(record, 'to', 'To', 'para', 'Para', 'destinatario', 'Destinatario');
  const body = pickMailString(record, 'body', 'Body', 'cuerpo', 'Cuerpo', 'template', 'Template');

  const attachments = record.attachments as ActividadMailData['attachments'] | undefined;
  const requeridoParaTerminar = record.requeridoParaTerminar as boolean | undefined;
  const acciones = record.acciones as ActividadMailData['acciones'] | undefined;

  if (!subject && !from && !to && !body && !attachments && !acciones?.length) {
    return undefined;
  }

  return {
    subject,
    from,
    to,
    body,
    attachments,
    requeridoParaTerminar,
    acciones,
  };
}

function parseActividadMailData(raw: unknown): ActividadMailData | undefined {
  if (raw == null) return undefined;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    try {
      return parseActividadMailData(JSON.parse(trimmed));
    } catch {
      try {
        return parseActividadMailData(JSON.parse(atob(trimmed)));
      } catch {
        return undefined;
      }
    }
  }

  if (Array.isArray(raw)) {
    const text = decodeBytesToText(raw as number[]);
    return text ? parseActividadMailData(text) : undefined;
  }

  if (typeof raw === 'object') {
    const record = raw as Record<string, unknown>;

    if (record.data != null) {
      const nested = parseActividadMailData(record.data);
      if (nested) return nested;
    }

    if (record.mailData != null) {
      const nested = parseActividadMailData(record.mailData);
      if (nested) return nested;
    }

    return recordToMailData(record);
  }

  return undefined;
}

function unwrapActividadMailDto(json: unknown): ActividadMailDTO {
  if (!json || typeof json !== 'object') {
    return (json ?? {}) as ActividadMailDTO;
  }

  const record = json as Record<string, unknown>;
  const looksLikeMailDto = (value: Record<string, unknown>) =>
    'status' in value || 'id' in value || 'usuarioActividadId' in value;

  if (looksLikeMailDto(record)) {
    return record as ActividadMailDTO;
  }

  if (record.data && typeof record.data === 'object' && !Array.isArray(record.data)) {
    const inner = record.data as Record<string, unknown>;
    if (looksLikeMailDto(inner)) {
      return inner as ActividadMailDTO;
    }
  }

  return record as ActividadMailDTO;
}

export function normalizeActividadMail(dto: ActividadMailDTO): ActividadMailDTO {
  const unwrapped = unwrapActividadMailDto(dto);
  return {
    ...unwrapped,
    data: parseActividadMailData(unwrapped.data),
  };
}

export function getActividadMailData(dto?: ActividadMailDTO | null): ActividadMailData | undefined {
  return parseActividadMailData(dto?.data);
}

export function mailDisplayValue(value?: string | null): string {
  const trimmed = value?.trim();
  return trimmed || '—';
}

export function getActividadMailFields(dto?: ActividadMailDTO | null): ActividadMailFields {
  const mailData = getActividadMailData(dto);
  return {
    subject: mailDisplayValue(mailData?.subject),
    from: mailDisplayValue(mailData?.from),
    to: mailDisplayValue(mailData?.to ?? dto?.to),
    body: mailData?.body ?? '',
  };
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function parseMailResponse(res: Response): Promise<ActividadMailDTO> {
  const text = await res.text().catch(() => '');
  if (!text) {
    throw new Error('Respuesta vacía del servidor.');
  }
  return normalizeActividadMail(JSON.parse(text) as ActividadMailDTO);
}

export async function fetchActividadMail(
  actividadId: string | number,
): Promise<ActividadMailDTO | null> {
  const res = await fetch(`/api/usuario/me/actividad/${actividadId}/mail`, {
    headers: { ...authHeader() },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(parseActividadApiError(res.status, text));
  }
  return parseMailResponse(res);
}

export async function sendActividadMail(
  actividadId: string | number,
  attachmentIds?: number[],
): Promise<ActividadMailDTO> {
  const body = attachmentIds?.length ? { attachmentIds } : {};
  const res = await fetch(`/api/usuario/me/actividad/${actividadId}/mail/send`, {
    method: 'POST',
    headers: {
      ...authHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const text = await res.text().catch(() => '');
  if (!res.ok) {
    throw new Error(parseActividadApiError(res.status, text));
  }
  if (!text) {
    throw new Error('Respuesta vacía al enviar el mail.');
  }
  return normalizeActividadMail(JSON.parse(text) as ActividadMailDTO);
}

export async function cerrarActividadMail(
  actividadId: string | number,
): Promise<ActividadMailDTO> {
  const res = await fetch(`/api/usuario/me/actividad/${actividadId}/mail/cerrar`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  const text = await res.text().catch(() => '');
  if (!res.ok) {
    throw new Error(parseActividadApiError(res.status, text));
  }
  if (!text) {
    throw new Error('Respuesta vacía al cerrar el mail.');
  }
  return normalizeActividadMail(JSON.parse(text) as ActividadMailDTO);
}

export function actividadMailStatusLabel(status?: ActividadMailStatus | string): string {
  switch (status) {
    case 'NUEVO': return 'Pendiente de envío';
    case 'ENVIADO': return 'Enviado';
    case 'CERRADO': return 'Cerrado';
    default: return status ?? '—';
  }
}
